import React from 'react';
import { useQuery } from '@apollo/client';
import { Link, useLocation } from 'react-router-dom';
import { ChartBarIcon } from '@heroicons/react/outline';
import { useStore } from 'context/store-context';
import { GET_REQUEST_ISSUE_METRICS } from 'gql/queries';
import { ReturnMetricsAggregate } from 'types/return';
import { withRequestReturnTo } from 'utils/requestNavigation';
import {
  RequestReportDateRange,
  useRequestReportDateRange,
} from './requestReportDateRange';
import { RequestBilling } from './RequestBilling';
import { ReturnsAnalyticsPanel } from './ReturnsAnalyticsPanel';
import { ProtectionReport } from './ProtectionReport';
import { OrderEditingActivity } from './OrderEditing/OrderEditingActivity';

const sections = [
  { key: 'protection', label: 'Delivery Guarantee' },
  { key: 'requests', label: 'Issues, Returns & Exchanges' },
  { key: 'order-editing', label: 'Order Editing' },
  { key: 'billing', label: 'Billing' },
];
const issueRows = [
  { key: 'totalClaims', label: 'Total Issues', status: undefined },
  { key: 'newClaims', label: 'New Issues', status: 'NEW' },
  { key: 'inReviewClaims', label: 'In review', status: 'IN_REVIEW' },
  { key: 'resolvedClaims', label: 'Resolved Issues', status: 'RESOLVED' },
];

function IssueStatusBreakdown({
  storeId,
  range,
}: {
  storeId: string;
  range: ReturnType<typeof useRequestReportDateRange>;
}) {
  const location = useLocation();
  const query = useQuery<Record<string, ReturnMetricsAggregate>>(
    GET_REQUEST_ISSUE_METRICS,
    {
      variables: {
        storeId,
        startDate: range.startDateTime,
        endDate: range.endDateTime,
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );
  const complete = issueRows.every(({ key }) => {
    const count = query.data?.[key]?.aggregate?.count;
    return (
      count != null && Number.isFinite(Number(count)) && Number(count) >= 0
    );
  });
  return (
    <details className="rounded-xl border border-gray-200 bg-white">
      <summary className="cursor-pointer px-5 py-4 text-sm font-semibold text-gray-900 focus-visible:outline-blue-500">
        Issue status breakdown
      </summary>
      <div className="border-t border-gray-100 px-5 pb-5 pt-4">
        <p className="mb-4 text-sm text-gray-500">
          Current status of Issues created in this period. Resolved counts do
          not represent resolution dates.
        </p>
        {query.loading ? (
          <p role="status" className="text-sm text-gray-500">
            Loading Issue data…
          </p>
        ) : query.error || !complete ? (
          <div role="alert" className="text-sm text-red-700">
            Issue data couldn&apos;t be loaded.{' '}
            <button
              type="button"
              onClick={() => {
                void query.refetch().catch(() => undefined);
              }}
              className="font-medium underline"
            >
              Retry Issue data
            </button>
          </div>
        ) : (
          <table
            aria-label="Issue status breakdown"
            className="w-full text-left text-sm"
          >
            <thead>
              <tr className="border-b border-gray-200 text-xs text-gray-500">
                <th scope="col" className="pb-2 font-medium">
                  Status
                </th>
                <th scope="col" className="pb-2 text-right font-medium">
                  Issues
                </th>
              </tr>
            </thead>
            <tbody>
              {issueRows.map(({ key, label, status }) => {
                const count = Number(query.data![key].aggregate!.count);
                const params = new URLSearchParams({
                  type: 'ISSUE',
                  group: 'ALL',
                  createdAfter: range.startDateTime,
                  createdBefore: range.exclusiveEndDateTime,
                });
                if (status) params.set('status', status);
                return (
                  <tr
                    key={key}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <th scope="row" className="py-3 font-medium text-gray-700">
                      {label}
                    </th>
                    <td className="py-3 text-right tabular-nums">
                      <Link
                        aria-label={`${label} ${count}`}
                        className="font-semibold text-blue-700 hover:underline"
                        to={withRequestReturnTo(
                          `/requests?${params}`,
                          `${location.pathname}${location.search}`,
                        )}
                      >
                        {count.toLocaleString()}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </details>
  );
}

export function RequestReports() {
  const { storeId } = useStore();
  const range = useRequestReportDateRange();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requested = params.get('report');
  const active =
    location.hash === '#return-billing'
      ? 'billing'
      : sections.some(({ key }) => key === requested)
      ? requested
      : 'requests';

  if (!storeId)
    return (
      <p className="text-sm text-gray-600">Select a store to view reports.</p>
    );

  return (
    <div className="min-w-0 space-y-6 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-blue-50 p-3 text-blue-700">
            <ChartBarIcon aria-hidden="true" className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
              Reports
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Explore Delivery Guarantee performance, return and exchange
              trends, and recorded billing events.
            </p>
          </div>
        </div>
        <RequestReportDateRange {...range} />
      </div>
      <nav
        aria-label="Report sections"
        className="flex flex-wrap gap-1 border-b border-gray-200"
      >
        {sections.map(({ key, label }) => {
          const next = new URLSearchParams(params);
          next.set('report', key);
          return (
            <Link
              key={key}
              to={{
                pathname: '/requests/reports',
                search: `?${next}`,
                hash: '',
              }}
              aria-current={active === key ? 'page' : undefined}
              className={`border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                active === key
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-900'
              }`}
            >
              {label}
            </Link>
          );
        })}
      </nav>
      <div key={`${storeId}:${active}`} className="space-y-6">
        {active === 'protection' && (
          <ProtectionReport
            startDate={range.startDateTime}
            endDate={range.endDateTime}
          />
        )}
        {active === 'requests' && (
          <>
            <ReturnsAnalyticsPanel
              storeId={storeId}
              startDate={range.startDateTime}
              endDate={range.exclusiveEndDateTime}
            />
            <IssueStatusBreakdown storeId={storeId} range={range} />
          </>
        )}
        {active === 'billing' && (
          <section id="return-billing" aria-label="Return and exchange billing">
            <RequestBilling dateRange={range} />
          </section>
        )}
        {active === 'order-editing' && (
          <OrderEditingActivity
            storeId={storeId}
            periodStart={range.startDateTime}
            periodEnd={range.exclusiveEndDateTime}
          />
        )}
      </div>
    </div>
  );
}
